import { useCheckoutContext } from '../index'
import { useAccessToken, useError } from '@ztechapps/common.hook'
import compact from 'lodash/compact'
import { fetch } from '@ztechapps/common.utils'
import isEmpty from 'lodash/isEmpty'
import trim from 'lodash/trim'

type StripeLineItem = { price: number; name: string; description?: string; quantity?: number }

export type BankTransferInfo = {
  bankName?: string
  routingNumber?: string
  accountNumber?: string
  swiftCode?: string
  amount: number
  currency: string
  reference?: string
}

export type BankTransferResult = {
  paymentIntentId: string
  paymentId: string
  block: string
  bankInfo: BankTransferInfo
}

export type UseCreateBankTransferFromOrderResult = {
  createBankTransfer: (partialLineItem?: StripeLineItem) => Promise<BankTransferResult>
  hasError: boolean
  error: any
}

const useCreateBankTransferFromOrderBase = (): UseCreateBankTransferFromOrderResult => {
  const checkoutContext = useCheckoutContext()
  const { document } = checkoutContext.orderDocumentResult
  const { error, onError } = useError()
  const fromCheckout = checkoutContext.fromCheckout
  const orderNetAmount = checkoutContext.orderNetAmount

  const [, setShowLoadingOverlay] = checkoutContext.showLoadingOverlay
  const accessToken = useAccessToken()
  const orderId = document?._id

  const createBankTransfer = async (partialLineItem?: StripeLineItem): Promise<BankTransferResult> => {
    try {
      setShowLoadingOverlay(true)

      const newLineItems = compact(isEmpty(partialLineItem) ? [] : [partialLineItem])

      const url = `/api/stripe/create-bank-transfer`

      const result: BankTransferResult = await fetch(url, {
        headers: {
          'login-token': accessToken,
        },
        method: 'POST',
        body: JSON.stringify({
          amount: orderNetAmount,
          orderId,
          userId: document?.by?._id,
          line_items: newLineItems,
          fromCheckout,
        }),
      })

      return result
    } catch (e) {
      onError(e)
      throw e
    } finally {
      setShowLoadingOverlay(false)
    }
  }

  return { createBankTransfer, hasError: !isEmpty(error), error }
}

export const useCreateBankTransferFromOrder = (): UseCreateBankTransferFromOrderResult => {
  const { createBankTransfer, ...rest } = useCreateBankTransferFromOrderBase()
  const checkoutContext = useCheckoutContext()
  const [paymentOptions] = checkoutContext.paymentOptions
  const hasPartialPayments = checkoutContext.hasPartialPayments

  const remarks = paymentOptions.remarks
  const amount = paymentOptions.amount
  const partialPaymentLineItem: StripeLineItem | undefined = hasPartialPayments
    ? {
        name: isEmpty(trim(remarks)) ? 'Additional payment.' : remarks,
        price: amount,
      }
    : undefined

  const _createBankTransfer = () => createBankTransfer(partialPaymentLineItem)

  return { ...rest, createBankTransfer: _createBankTransfer }
}

export const useCreatePartialBankTransferFromOrder = (): UseCreateBankTransferFromOrderResult => {
  const checkoutContext = useCheckoutContext()
  const [paymentOptions] = checkoutContext.paymentOptions

  const { createBankTransfer, ...rest } = useCreateBankTransferFromOrderBase()

  const _createBankTransfer = (partialLineItem?: StripeLineItem) => {
    const remarks = partialLineItem?.name ?? paymentOptions.remarks
    const amount = partialLineItem?.price ?? paymentOptions.amount
    const lineItem: StripeLineItem = {
      name: isEmpty(trim(remarks)) ? 'Additional payment.' : remarks,
      price: amount,
      ...(partialLineItem?.description && { description: partialLineItem.description }),
      ...(partialLineItem?.quantity && { quantity: partialLineItem.quantity }),
    }

    return createBankTransfer(lineItem)
  }

  return { ...rest, createBankTransfer: _createBankTransfer }
}
